/**
 * Source unique de vérité pour les URLs médecins/centres.
 *
 * Pattern Doctolib :
 *   - Médecin individuel : /medecin/{ville-slug}/{prenom-nom}
 *   - Centre dentaire    : /centre-dentaire/{ville}/{slug}
 *   - Hôpital            : /hopital/{ville}/{slug}
 *   - Clinique privée    : /clinique/{ville}/{slug}
 *   - etc.
 *
 * Le slug du médecin est extrait du firstName + lastName, SANS le préfixe
 * "dr-" ni la spécialité (déjà encodée en path via le routeur).
 */
import { CENTER_ROUTES, inferClinicType, type ClinicTypeKey } from "./center-types";
import { SPECIALTIES, CITIES } from "./specialties";
import { cityFromQuartierSlug, QUARTIERS } from "./quartiers";
import type { Physician } from "./physicians";
import PHYSICIAN_SLUG_REDIRECTS_JSON from "./physician-slug-redirects.json";

// Sets pré-calculés pour validation rapide des slugs de route.
// La route /[specialite]/[ville]/[name] notFound() si le slug n'est pas
// dans ces sets — on retombe alors sur /medecin/{slug} qui résout
// directement par DB sans contrainte route.
const CANONICAL_SPE_SLUGS = new Set(SPECIALTIES.map((s) => s.slug));
const CANONICAL_CITY_SLUGS = new Set(CITIES.map((c) => c.slug));

/**
 * Slugs de spécialité dont le SEGMENT DE ROUTE appartient à une AUTRE entité.
 * Un praticien portant l'une de ces spécialités ne peut PAS être servi en
 * /{spe}/{ville}/{nom} — il bascule sur le fallback /medecin/{slug}.
 *
 * `pharmacien` : la route statique [locale]/pharmacien/[ville]/[slug] sert les
 * OFFICINES (table PharmacyOnCall, 4 123 entités). En Next.js le segment
 * statique gagne sur le dynamique [specialite], donc /pharmacien/{ville}/{nom}
 * cherche une officine nommée {nom}, n'en trouve pas, et retombe sur son
 * fallback 301 → /pharmacie-de-garde/{ville}.
 *
 * Incident 2026-08-17 : les 10 fiches pharmacien de la base (dont une GOLD)
 * étaient toutes injoignables via une chaîne à 2 sauts
 * /medecin/{slug} → /pharmacien/{ville}/{nom} → /pharmacie-de-garde/{ville}.
 * physicianUrl() émettait l'URL /pharmacien/… que la route ne pouvait servir.
 *
 * NE PAS retirer `pharmacien` d'ici sans avoir d'abord fait cohabiter les deux
 * entités sur ce segment (officine ET pharmacien titulaire).
 */
export const URL_RESERVED_SPE_SLUGS = new Set(["pharmacien"]);

/**
 * Canonical `/[spe]/[ville]/[nom]` reconstruit depuis un slug DB legacy
 * (`dr-{name}-{spe}-{ville}`). Retourne undefined si le slug n'est pas
 * parsable OU si la spécialité occupe un segment de route possédé par une
 * autre entité (URL_RESERVED_SPE_SLUGS) — dans ce cas `/medecin/{slug}` EST
 * le canonical.
 *
 * SEUL constructeur autorisé de ce path à partir d'un parseLegacySlug() :
 * consommé par proxy.ts (301 à la volée) et par le générateur de sitemap.
 * Un appel direct à parseLegacySlug() suivi d'une concaténation manuelle
 * ré-introduit le bug des URLs non servables (incident pharmacien 2026-08-17 :
 * 301 émis vers /pharmacien/{ville}/{nom} → 301 /pharmacie-de-garde/{ville}).
 */
export function legacyCanonicalFromSlug(slug: string): string | undefined {
  const parsed = parseLegacySlug(slug);
  if (!parsed.isLegacy || !parsed.specialtySlug || !parsed.citySlug || !parsed.nameSlug) {
    return undefined;
  }
  if (URL_RESERVED_SPE_SLUGS.has(parsed.specialtySlug)) return undefined;
  return `/${parsed.specialtySlug}/${parsed.citySlug}/${parsed.nameSlug}`;
}

/**
 * Alias de spécialités → slug canonique. Sert UNIQUEMENT à la résolution
 * d'URL : on consolide les paires synonymes (titre formel vs terme grand
 * public) sur un seul slug pour éviter le "Duplicate, Google chose different
 * canonical" qu'on prend en GSC (~40-50 cas mai 2026).
 *
 * Règle : la valeur (slug canonique) doit rester featured=true dans
 * SPECIALTIES ; la clé (alias) peut rester en DB et dans SPECIALTIES en
 * featured=false — seule la SURFACE URL est consolidée. Le proxy.ts redirige
 * /{alias}/* → /{canonical}/* en 301, le sitemap drop les pages /{alias}/*.
 *
 * 2026-05-29 : chirurgien-dentiste → dentiste (Sprint 6 audit Phase 2 #10).
 * 1 194 médecins ont chirurgien-dentiste en specialties[0] (vs 7 225 dentiste)
 * + 1 164 entrées redirect-map ciblent /chirurgien-dentiste/. dentiste reste
 * featured=true (terme top-searched, cf project_competitive_seo_plan).
 */
const SPECIALTY_ALIASES: Record<string, string> = {
  "chirurgien-dentiste": "dentiste",
  // 2026-06 : casser boucle 301↔308 pour specs primaires hors SPE_SLUGS featured.
  // phlebologue → medecine-interne (proche cliniquement, slug canonique present)
  // urgentiste  → generaliste (medecins urgentistes en cabinet = generalistes)
  phlebologue: "medecine-interne",
  urgentiste: "generaliste",
  // Fix P0 2026-07-06 (audit classification pages medecin) : 10 medecins en
  // DB ont specialty="medecine-alternative" (terme generique scraper, pas
  // une specialite avec hub dedie). acupuncteur est la modalite la plus
  // proche deja modelisee avec son propre hub (34 medecins) — evite de
  // creer un hub thin-content pour seulement 10 fiches sans classification
  // plus precise disponible en DB.
  "medecine-alternative": "acupuncteur",
};

/**
 * Résout un slug de spécialité vers son slug canonique URL. Si le slug n'a
 * pas d'alias, retourne tel quel (identité). À appliquer dans TOUT code qui
 * émet une URL médecin ou compare une spécialité à celle de l'URL résolue
 * (sinon boucle 301↔308 : proxy redirige /chirurgien-dentiste/X → /dentiste/X
 * mais la page resolveSlug renvoie correctSpe=chirurgien-dentiste et renvoie
 * en 308 vers /chirurgien-dentiste/X). Cf MEMORY architecture_url_medecins.
 */
export function canonicalSpecialty(slug: string): string {
  return SPECIALTY_ALIASES[slug] ?? slug;
}

/**
 * Reverse de SPECIALTY_ALIASES : tous les specialtySlugs bruts (DB) qui se
 * résolvent vers `specialtySlug` canonique. Miroir de `aliasCitySlugsFor()`
 * — même besoin : le champ DB Physician.specialties peut porter le slug BRUT
 * ("chirurgien-dentiste") alors que l'URL/tracking utilise toujours le slug
 * CANONIQUE ("dentiste"). Fix 2026-07-20 (audit "Fiche introuvable" admin).
 */
export function aliasSpecialtySlugsFor(specialtySlug: string): string[] {
  return Object.keys(SPECIALTY_ALIASES).filter((k) => SPECIALTY_ALIASES[k] === specialtySlug);
}

/**
 * Alias de citySlug DB → ville canonique. Sert UNIQUEMENT à la résolution
 * d'URL (canonicalCity), PAS à générer des pages hub (contrairement à
 * QUARTIERS). On y met donc :
 *   - les fautes d'orthographe de villes (essaouirra → essaouira)
 *   - les districts/avenues qu'on ne veut PAS exposer en page /[spe]/[ville]/[quartier]
 *     mais qu'on sait rattacher à leur ville mère.
 * Source : audit 2026-05-22 des cityId en fallback /medecin/[slug].
 */
const CITY_ALIASES: Record<string, string> = {
  // fautes d'orthographe
  essaouirra: "essaouira",
  skhirate: "skhirat",
  taroudannt: "taroudant",
  // 2026-06 : alias coordonnes avec dedup CITIES (agent A2)
  "al-kelaa-des-sraghna": "el-kelaa-des-sraghna",
  "es-semara": "smara",
  // Le slug canonique en CITIES est "fquih-ben-salah" (h final), pas "saleh".
  "fkih-ben-saleh": "fquih-ben-salah",
  "fkih-ben-salah": "fquih-ben-salah",
  "fquih-ben-saleh": "fquih-ben-salah",
  // districts / artères → ville mère (pas de page hub dédiée)
  "bd-abdelmoumen": "casablanca",
  "bd-zerktouni": "casablanca",
  "quartier-des-hopitaux": "casablanca",
  "sidi-moumen": "casablanca",
  "el-oulfa": "casablanca",
  "madinat-al-irfane": "rabat",
  "quartier-hassan": "rabat",
  "sala-al-jadida": "sale",
  "route-de-targa": "marrakech",
  targa: "marrakech",
  "ain-chok": "casablanca",
  sbata: "casablanca",
  "bd-youssef-ibn-tachfine": "casablanca",
  "bd-brahim-roudani": "casablanca",
  souissi: "rabat",
  "hay-ryad": "rabat",
  saknia: "kenitra",
  "beni-makada": "tanger",
  bernoussi: "casablanca",
  "sidi-bernoussi": "casablanca",
  "sidi-othmane": "casablanca",
  "bd-bir-anzarane": "casablanca",
  "temara-centre": "temara",
  // 2026-06-22 : alias slugs courts/variantes → canoniques CITIES
  "souk-sebt": "souk-sebt-oulad-nemma",
  tahanaout: "tahannaout",
  imouzer: "imouzzer-kandar",
  // 2026-09-08 : CITIES contient encore deux entrées pour la même ville
  // (mêmes coordonnées) — la variante ici est l'alias, l'autre la canonique.
  // On ne supprime pas l'entrée CITIES (la route /villes/[ville] répondrait
  // 404 au lieu de 308) ; on la résout ici et on ne l'émet plus au sitemap.
  "mohammed-v-airport": "nouaceur",
  tifelt: "tiflet",
  // 2026-09-09 : quartier de Témara promu à tort au rang de ville par un
  // import de garde — deux URLs auto-canoniques pour la même garde Harhoura,
  // 20 fiches d'officine doublonnant celles de Témara. Données fusionnées
  // dans `temara` (district Harhoura), URLs pharmacie redirigées dans
  // proxy.ts (MERGED_CITY_PATHS). L'entrée CITIES reste (308, pas 404).
  "temara-harhoura": "temara",
  // 2026-09-09 : quatre paires de plus, même motif (deux entrées CITIES pour
  // une seule ville). psql du 2026-09-09 : bejaad, beni-enzar et souk-el-arbaa
  // sont à 0 officine ET 0 médecin ; benguerir, lui, porte 3 fiches médecin
  // (contre 20 côté ben-guerir) qui passeront donc en 308 sur
  // /{spe}/ben-guerir/{nom} — migration assumée, elle est du bon côté.
  // Vérifié avant de poser l'alias : physician-slug-redirects.json ne contient
  // aucune occurrence de /benguerir/, /bejaad/, /beni-enzar/, /souk-el-arbaa/
  // ni /aoulouz/, donc pas de régénération de la carte de redirections.
  // On ne supprime rien de CITIES, on résout ici — /villes/{alias} passe en 308
  // et sort du sitemap, la page cible est strictement plus riche.
  //   benguerir      → ben-guerir             (3 médecins vs 20 + 34 officines ;
  //                    /ar/villes/benguerir = 544 impr / 13 clics, pos 9,1)
  //   beni-enzar     → beni-ansar             (0 impression mesurée sur 28 j)
  //   bejaad         → boujad                 (150 m d'écart, boujad = cible du
  //                    scraper avec 195 gardes ; bejaad à 0 officine)
  //   souk-el-arbaa  → souk-el-arba-du-gharb  (500 m, 23 officines côté cible ;
  //                    /ar/villes/souk-el-arbaa = 142 impr / 7 clics)
  // NON traité ici : benslimane → ben-slimane. Les deux entrées existent aussi,
  // mais 183 médecins ont leur fiche sous /{spe}/benslimane/{nom} (49 URLs,
  // 697 impressions / 102 clics sur 28 j) et 15 lignes de
  // physician-slug-redirects.json y pointent : l'alias déclencherait une
  // migration d'URL de 183 fiches + une régénération de la carte de
  // redirections. À traiter dans un lot « médecins », pas ici.
  benguerir: "ben-guerir",
  "beni-enzar": "beni-ansar",
  bejaad: "boujad",
  "souk-el-arbaa": "souk-el-arba-du-gharb",
  // Le slug CITIES est « aouli » mais l'entrée porte nameFr « Aoulouz » et les
  // coordonnées d'Aoulouz (30,6806 / -8,1664) ; OSM et la source écrivent
  // « Aoulouz ». On ne renomme pas le slug (301 à poser sur des URLs déjà
  // indexées) : on accepte la graphie réelle en entrée.
  aoulouz: "aouli",
};

/**
 * Résout un citySlug DB vers un slug ville canonique. Cascade :
 *   1. Le slug est déjà une ville canonique → retourne tel quel
 *   2. Le slug est un quartier connu (agdal, maarif, gueliz, hay-riad, …)
 *      → retourne son citySlug parent (rabat, casablanca, marrakech)
 *   3. Le slug est un alias (faute/district) → ville canonique mappée
 *   4. Aucun mapping → retourne null (caller doit fallback)
 *
 * Évite que les ~4 591 médecins en prod (mai 2026) avec un cityId pointant
 * vers un quartier soient relégués sur /medecin/{slug} alors qu'on peut
 * leur donner une URL Doctolib propre `/{spe}/{villeMere}/{name}`.
 */
/**
 * Reverse de CITY_ALIASES : tous les citySlugs bruts (fautes/districts) qui se
 * résolvent vers `citySlug` canonique. Utilisé par la page /[spe]/[ville]/[nom]
 * pour inclure ces fiches dans les candidats (sinon physicianUrl émet une URL
 * canonique que la page ne sait pas servir → BOUCLE de redirection).
 */
export function aliasCitySlugsFor(citySlug: string): string[] {
  return Object.keys(CITY_ALIASES).filter((k) => CITY_ALIASES[k] === citySlug);
}

/**
 * Tous les citySlug BRUTS du catalogue qui appartiennent à la ville canonique
 * `citySlug` : le slug lui-même, ses fautes d'orthographe (CITY_ALIASES) et ses
 * quartiers (QUARTIERS).
 *
 * À utiliser dans tout filtre de listing à la place de `p.citySlug === ville`.
 *
 * Pourquoi : physicianUrl() construit l'URL du médecin avec canonicalCity(),
 * qui résout fautes et quartiers ; les hubs, eux, comparaient le slug DB brut.
 * Les deux ne coïncidaient pas. Un médecin dont le citySlug DB est `taroudannt`
 * a pour URL canonique /{spe}/taroudant/{nom}, mais n'apparaissait pas sur
 * /{spe}/taroudant — et /{spe}/taroudannt redirige (301) vers ce même listing
 * qui ne le contient pas : la fiche n'était listée nulle part.
 *
 * Mesure du 2026-09-05 sur le catalogue : 1 186 médecins masqués, 181 combos
 * spécialité×ville touchés. Cas extrêmes — /generaliste/el-kelaa-des-sraghna
 * affichait 0 médecin pour 142 existants (tous sur `al-kelaa-des-sraghna`),
 * /generaliste/taroudant 46 pour 202, /generaliste/essaouira 34 pour 122.
 */
const RAW_SLUGS_BY_CITY: Map<string, Set<string>> = (() => {
  const m = new Map<string, Set<string>>();
  const add = (canonical: string, raw: string) => {
    if (!CANONICAL_CITY_SLUGS.has(canonical)) return;
    const set = m.get(canonical) ?? new Set<string>([canonical]);
    set.add(raw);
    m.set(canonical, set);
  };
  for (const c of CANONICAL_CITY_SLUGS) add(c, c);
  for (const [raw, canonical] of Object.entries(CITY_ALIASES)) add(canonical, raw);
  for (const q of QUARTIERS) add(q.citySlug, q.slug);
  return m;
})();

export function rawCitySlugsFor(citySlug: string): ReadonlySet<string> {
  return RAW_SLUGS_BY_CITY.get(citySlug) ?? new Set([citySlug]);
}

/**
 * Prédicat de listing : ce médecin appartient-il à la ville canonique `city` ?
 * Remplace `p.citySlug === city`.
 */
export function physicianIsInCity(rawCitySlug: string | null | undefined, city: string): boolean {
  return rawCitySlug != null && rawCitySlugsFor(city).has(rawCitySlug);
}

export function canonicalCity(slug: string): string | null {
  // Un alias EXPLICITE l'emporte sur l'appartenance à CITIES : trois villes y
  // figurent deux fois (souk-sebt / souk-sebt-oulad-nemma, mohammed-v-airport /
  // nouaceur, tifelt / tiflet). Sans ce test en tête, canonicalCity("tifelt")
  // renvoyait "tifelt" et l'alias ne servait à rien — 2 pages /villes/
  // identiques soumises au sitemap (motif GSC « Google a choisi une autre
  // canonique », 2026-09-08). Aucun médecin n'est rattaché aux 3 alias.
  const explicitAlias = CITY_ALIASES[slug];
  if (explicitAlias && CANONICAL_CITY_SLUGS.has(explicitAlias)) return explicitAlias;
  if (CANONICAL_CITY_SLUGS.has(slug)) return slug;
  const parent = cityFromQuartierSlug(slug);
  if (parent && CANONICAL_CITY_SLUGS.has(parent)) return parent;
  const alias = CITY_ALIASES[slug];
  if (alias && CANONICAL_CITY_SLUGS.has(alias)) return alias;
  return null;
}

/**
 * Tente d'extraire une ville canonique depuis une `addressLine1` brute scrapée.
 *
 * Sert UNIQUEMENT de filet pour les fiches dont le `citySlug` DB est pollué
 * (chaîne trop longue, faute non aliasée, etc.) — on évite le rendu avec
 * self-canonical en redirigeant vers /{spe}/{villeMere} (cf. fallback
 * /medecin/[slug] page.tsx).
 *
 * Algorithme naïf : recherche le nameFr / nameAr / nameEn / slug de chaque
 * CitySeed comme sous-chaîne case-insensitive dans l'adresse. Priorité aux
 * featured pour réduire les faux positifs (ex. "Tanger" matche dans
 * "Bd Tanger" mais une fiche à Casablanca avec cette rue serait mal classée).
 * Retourne le slug canonique de la première ville matchée, sinon null.
 *
 * Performance : O(|CITIES| × |address|) ≈ 70 × 50 caractères → négligeable
 * et appelé uniquement dans le path fallback /medecin/[slug] (≤ 5% requêtes).
 */
export function inferCityFromAddress(addressLine1: string | null | undefined): string | null {
  if (!addressLine1 || typeof addressLine1 !== "string") return null;
  const norm = addressLine1
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");
  if (!norm) return null;

  // Pass 1 : villes featured (Top 12 grandes métropoles + extension nationale
  // explicitement marquées featured=true). Faible risque de collision.
  for (const city of CITIES) {
    if (!city.featured) continue;
    const nameFrNorm = city.nameFr.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
    if (norm.includes(nameFrNorm) || norm.includes(city.slug)) {
      return city.slug;
    }
  }

  // Pass 2 : villes non-featured. Garde-fou : on exige une frontière de mot
  // (espace, virgule, début/fin) pour éviter "safi" qui matcherait dans
  // "asfi" ou autres sous-chaînes courtes.
  for (const city of CITIES) {
    if (city.featured) continue;
    const nameFrNorm = city.nameFr.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
    const boundaryRe = new RegExp(`(?:^|[^a-z])(?:${nameFrNorm}|${city.slug})(?:[^a-z]|$)`);
    if (boundaryRe.test(norm)) {
      return city.slug;
    }
  }

  return null;
}

/**
 * Décode les entités HTML les plus courantes laissées par le scraper.
 * Sans ça, "Sanaa N&#39;hammoucha" produirait le slug `sanaa-n39hammoucha`
 * (le `39` est conservé car alphanumérique), URL ensuite indexée et 404
 * lors du re-resolve. Crawler 500 mai 2026 : 0.6 % des cas.
 */
function decodeHtmlEntities(s: string): string {
  return s
    .replace(/&#0*39;|&apos;/gi, "'")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#0*34;/gi, '"')
    .replace(/&nbsp;/gi, " ");
}

/**
 * Slugify un nom : minuscules, accents retirés, espaces → tirets.
 */
export function slugifyName(input: string): string {
  return decodeHtmlEntities(input)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "") // retire les accents
    .replace(/[^a-z0-9\s-]/g, "") // garde uniquement alphanum + espaces + tirets
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

/**
 * STRONG parasite tokens — toujours strippes au debut du nom.
 * Specialty nouns + roles + institutions. Si le scraper a pollue lastName
 * avec "Psychiatrie Dr X Y", ces tokens disparaissent.
 */
const STRONG_PARASITE_TOKENS = new Set([
  // Specialty nouns (FR + EN)
  "psychiatrie", "cardiologie", "dermatologie", "gynecologie",
  "pediatrie", "ophtalmologie", "neurologie", "rhumatologie",
  "endocrinologie", "gastroenterologie", "pneumologie", "nephrologie",
  "orthopedie", "kinesitherapie", "podologie", "stomatologie",
  "dentaire", "esthetique", "radiologie", "orthoptie",
  "chirurgie", "anesthesie", "biologie", "hematologie", "oncologie",
  "pharmacie", "allergologie", "neurochirurgie", "urologie", "sexologie",
  "nutrition", "addictologie", "andrologie", "angiologie",
  "hepatologie", "geriatrie", "diabetologie", "phlebologie",
  "psychiatry", "cardiology", "dermatology", "neurology", "pediatrics",
  // Specialty roles
  "chirurgien", "medecin", "dentiste", "biologiste", "anesthesiste",
  "psychologue", "infirmier", "opticien", "orthophoniste",
  "kinesitherapeute", "osteopathe", "nutritionniste", "pediatre",
  "gynecologue", "cardiologue", "dermatologue", "ophtalmologue",
  "neurologue", "rhumatologue", "endocrinologue", "pneumologue",
  "nephrologue", "orthopediste", "podologue", "stomatologue",
  "radiologue", "anatomopathologiste", "orthodontiste",
  "urologue", "psychiatre", "geriatre", "diabetologue",
  "hematologue", "oncologue", "pharmacien", "allergologue",
  "neurochirurgien", "sexologue", "andrologue", "angiologue",
  "hepatologue", "hypnotherapeute", "pedopsychiatre",
  "generaliste", "interniste", "urgentiste", "phlebologue",
  // Institution
  "centre", "cabinet", "clinique", "polyclinique", "hopital",
  "hospital", "service", "departement", "pole", "ecole", "groupe",
  // Titles (without dot — dot stripped before tokenize)
  "dr", "dre", "drs", "docteur", "doctor", "doc",
  "professeur", "prof", "professor", "pr",
  "mlle", "mme", "mr", "monsieur", "madame", "mademoiselle",
]);
/** WEAK parasites — articles : strippes UNIQUEMENT si suivi d'un STRONG.
 *  Empeche de strip "El Mokri" ou "Al-Rashid" qui sont des vrais noms. */
const WEAK_PARASITE_TOKENS = new Set([
  "de", "du", "des", "le", "la", "les", "el", "al", "d", "l",
  "the", "a",
]);

/**
 * Clean parasite prefixes in physician firstName/lastName.
 *
 * Scraper pollution example :
 *   firstName: "de", lastName: "Psychiatrie Dr Safae Khilaji"
 *   → cleaned : firstName: "Safae", lastName: "Khilaji"
 *
 * Algorithm en 3 passes (apres strip "Dr."/"Dre." token-aware) :
 *   1. Strip STRONG parasite tokens en debut
 *   2. Strip WEAK parasite (article) SI suivi d'un STRONG ou autre WEAK
 *   3. Strip STRONG residuels apres etape 2
 *
 * Preserve les vrais noms commencant par article :
 *   "El Mokri Mohamed" → firstName="El", lastName="Mokri Mohamed" (no strip)
 *   "Hassan Al-Rashid" → firstName="Hassan", lastName="Al-Rashid" (Al-Rashid token compound)
 *
 * Si tout est strippe (cas degenere), retourne l'input original.
 */
export function cleanPhysicianName(
  firstName: string | null | undefined,
  lastName: string | null | undefined,
): { firstName: string; lastName: string } {
  const origFirst = firstName ?? "";
  const origLast = lastName ?? "";
  const raw = `${origFirst} ${origLast}`.trim();
  if (!raw) return { firstName: origFirst, lastName: origLast };

  // Strip "Dr." / "Dre." / "Pr." tokens (preserve casse pour le reste)
  const cleanedRaw = raw
    .replace(/\b(Dr|Dre|Drs|Pr|Prof)\.?\b/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!cleanedRaw) return { firstName: origFirst, lastName: origLast };

  let tokens = cleanedRaw.split(/\s+/).filter(Boolean);
  const normalize = (t: string) =>
    t.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[.,;:]/g, "");

  // Pass 1 : strip STRONG en debut
  while (tokens.length > 0 && STRONG_PARASITE_TOKENS.has(normalize(tokens[0]!))) {
    tokens.shift();
  }
  // Pass 2 : strip WEAK SI suivi de STRONG ou WEAK (signe pollution)
  while (
    tokens.length > 1 &&
    WEAK_PARASITE_TOKENS.has(normalize(tokens[0]!)) &&
    (STRONG_PARASITE_TOKENS.has(normalize(tokens[1]!)) ||
      WEAK_PARASITE_TOKENS.has(normalize(tokens[1]!)))
  ) {
    tokens.shift();
  }
  // Pass 3 : strip STRONG residuels
  while (tokens.length > 0 && STRONG_PARASITE_TOKENS.has(normalize(tokens[0]!))) {
    tokens.shift();
  }

  if (tokens.length === 0) return { firstName: origFirst, lastName: origLast };
  if (tokens.length === 1) return { firstName: tokens[0]!, lastName: "" };
  return { firstName: tokens[0]!, lastName: tokens.slice(1).join(" ") };
}

/**
 * Slug propre pour un médecin : "{prenom}-{nom}" sans "dr-" ni spécialité ni ville.
 *
 * Applique cleanPhysicianName() pour eliminer les parasites scraper avant slugify.
 *
 * Ex:
 *   "Dr Oulaya Bouali" → "oulaya-bouali"
 *   "Dr. Amine El Gallaf" → "amine-el-gallaf"
 *   firstName="de", lastName="Psychiatrie Dr Safae Khilaji" → "safae-khilaji"
 */
/**
 * Strip suffix adresse parasite : tokens d'adresse postale qui ont pollue
 * lastName cote scraper (ex. "Dupont avenue mohammed v" → slug "dupont").
 * Capture du premier token adresse jusqu'a fin du slug.
 */
const SUFFIX_NOISE = /(-avenue-|-bd-|-rue-|-quartier-|-bab-|-residence-|-lotissement-|-immeuble-|-angle-).*$/i;

export function physicianNameSlug(firstName: string, lastName: string): string {
  const cleaned = cleanPhysicianName(firstName, lastName);
  let slug = slugifyName(`${cleaned.firstName} ${cleaned.lastName}`);
  slug = slug.replace(SUFFIX_NOISE, "");
  return slug;
}

/**
 * Variante "legacy" qui ne décode PAS les entités HTML — utilisé pour
 * matcher les vieilles URLs déjà indexées par Google qui ont conservé
 * la trace de l'encodage (`sanaa-n39hammoucha` au lieu de `sanaa-nhammoucha`,
 * où le `39` vient de `&#39;` interprété comme alphanumérique). À utiliser
 * uniquement dans les fallbacks de résolution, pas pour générer des URLs
 * neuves.
 */
export function physicianNameSlugLegacy(firstName: string, lastName: string): string {
  return `${firstName} ${lastName}`
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

/**
 * URL canonique d'un médecin individuel.
 *
 * Validation stricte : la route `/[specialite]/[ville]/[name]` notFound()
 * si specialite ∉ SPECIALTIES.slug ou ville ∉ CITIES.slug. En prod, ~13 %
 * des médecins (5 500/41 600 en mai 2026) ont soit une ville orpheline
 * (`taroudannt`, `agdal`, `hay-riad`, `gueliz`, …), soit aucune spécialité
 * associée — une URL Doctolib-style construite naïvement renvoie 404.
 *
 * Stratégie de fallback :
 *   - Si le slug propre du médecin (`firstName-lastName`) résout → URL
 *     Doctolib `/{spe}/{city}/{name}` (uniquement si spe ET city sont
 *     dans les sets canoniques).
 *   - Sinon → `/medecin/{slug}` qui résout par DB sans contrainte de
 *     route et reste accessible et indexable.
 *
 * Ex propres :
 *   /dentiste/temara/oulaya-bouali
 *   /medecin-generaliste/casablanca/aali-imesty
 *
 * Ex fallback :
 *   /medecin/dr-sondos-al-rajab-chirurgien-maxillofacial-sale-medina
 *   /medecin/dr-mohammed-amine-dahik-casablanca   (médecin sans spe)
 */
export function physicianUrl(p: {
  firstName: string;
  lastName: string;
  citySlug: string;
  specialtySlug?: string | null;
  /** Slug DB complet du médecin — requis pour le fallback /medecin/{slug}. */
  slug?: string;
}): string {
  // Canonicalize d'abord : si la fiche DB a `chirurgien-dentiste`, on émet
  // directement `/dentiste/...` au lieu de laisser le proxy faire un 301
  // (qui coûte 1 hop SEO + crée une fenêtre 301↔308 en cas de déploiement
  // non atomique avec resolveSlug page.tsx).
  const canonicalSpe =
    p.specialtySlug ? canonicalSpecialty(p.specialtySlug) : null;
  // URL_RESERVED_SPE_SLUGS : segment possédé par une autre entité (officines
  // sur /pharmacien/…) → on n'émet pas cette URL, fallback /medecin/{slug}.
  const speOk =
    canonicalSpe !== null &&
    CANONICAL_SPE_SLUGS.has(canonicalSpe) &&
    !URL_RESERVED_SPE_SLUGS.has(canonicalSpe);
  // canonicalCity résout en cascade : ville canonique > quartier→ville parent.
  // Récupère ainsi automatiquement la ville mère pour les médecins dont
  // l'adresse a été scrapée avec un quartier comme cityId (agdal → rabat,
  // maarif → casablanca, gueliz → marrakech, etc.).
  const cityCanonical = canonicalCity(p.citySlug);

  if (speOk && cityCanonical) {
    const name = physicianNameSlug(p.firstName, p.lastName);
    return `/${canonicalSpe}/${cityCanonical}/${name}`;
  }

  // Fallback /medecin/{slug-DB} : cette route lit Physician par slug et ne
  // valide rien d'autre — elle marche pour les 100 % des médecins.
  if (p.slug) return `/medecin/${p.slug}`;

  // Dernier recours quand le slug DB n'est pas fourni : on tente quand même
  // le format Doctolib avec un placeholder `medecin` (au lieu de la spe
  // invalide). La route 404 si city pas dans CITY_SLUGS — c'est moins pire
  // que renvoyer un href vide.
  const name = physicianNameSlug(p.firstName, p.lastName);
  const segment = speOk ? canonicalSpe : "medecin";
  const cityFallback = cityCanonical ?? p.citySlug;
  return `/${segment}/${cityFallback}/${name}`;
}

/**
 * URL canonique d'une fiche médecin — LA fonction à utiliser partout où l'on
 * déclare ou compare un canonical (sitemap, <link rel="canonical">, 308 de la
 * route /{spé}/{ville}/{nom}). Cascade, dans cet ordre :
 *   1. physician-slug-redirects.json (fusions de doublons, noms nettoyés) —
 *      source de vérité, cf. reference_sitemap_redirect_map ;
 *   2. le slug legacy `dr-{nom}-{spé}-{ville}` reparsé ;
 *   3. physicianUrl() sur les champs.
 * Avant le 2026-09-21 seul le sitemap appliquait 1 → 2 → 3 ; la page et ses
 * métadonnées n'appliquaient que 3, d'où 644 fiches (214 indexables) dont le
 * canonical déclaré différait de l'URL émise au sitemap, et des fiches
 * « perdantes » de fusion servies en 200 sur leur ancienne URL.
 * Une cible `/medecin/…` de la carte est ignorée (comme au sitemap) : on
 * retombe sur 2 → 3.
 */
export function physicianCanonicalPath(p: {
  firstName: string;
  lastName: string;
  citySlug: string;
  specialtySlug?: string | null;
  slug: string;
}): string {
  const mapped = (PHYSICIAN_SLUG_REDIRECTS_JSON as Record<string, string>)[p.slug];
  if (mapped && !mapped.startsWith("/medecin/")) return mapped;
  const legacy = legacyCanonicalFromSlug(p.slug);
  if (legacy) return legacy;
  return physicianUrl(p);
}

/**
 * Variante de `physicianUrl` qui accepte un `name` string complet (ex.
 * "Dr Mohammed Amine Dahik") au lieu de firstName/lastName séparés.
 * Utilisé par les composants côté client qui consomment les API
 * `/api/search/suggest` (PhysicianSuggestion) où le nom n'est pas séparé.
 */
export function physicianUrlFromHit(p: {
  slug: string;
  name: string;
  citySlug: string;
  specialtySlug?: string | null;
  isCenter?: boolean;
}): string {
  // Centres : URL `/centre/{slug}` (ne dépend pas du nom — le slug DB du
  // centre contient déjà tout, ex. `cabinet-dentaire-dr-bouali-temara`).
  if (p.isCenter) return `/centre/${p.slug}`;
  const cleanName = p.name.replace(/^(?:dr|pr|prof|docteur|professeur)\.?\s+/i, "").trim();
  const [firstName, ...rest] = cleanName.split(/\s+/);
  return physicianUrl({
    firstName: firstName || "",
    lastName: rest.join(" "),
    citySlug: p.citySlug,
    specialtySlug: p.specialtySlug,
    slug: p.slug,
  });
}

/**
 * URL canonique d'un centre/cabinet/hôpital.
 * Format : /{type-segment}/{slug}  (1-level, aligné sur le routing
 * Next.js existant `/centre/[slug]` etc.)
 *
 * Bug fixé 2026-05-08 : retournait auparavant `/{segment}/{citySlug}/{slug}`
 * (3-level), provoquant 404 systématique car aucune route Next 3-level
 * n'existe pour les centres. Identifié via logs nginx — GPTBot suivait les
 * liens des cards `PhysicianCardRanked` qui appellent `canonicalUrl(p)` →
 * `centerUrl()` → 404. La param `citySlug` reste acceptée pour compat
 * d'API mais elle n'est plus utilisée dans la construction de l'URL.
 */
export function centerUrl(c: {
  slug: string;
  citySlug?: string;
  clinicType?: ClinicTypeKey | null;
}): string {
  const segment = c.clinicType
    ? CENTER_ROUTES[c.clinicType]?.pathSegment ?? "centre"
    : "centre";
  // Le slug du centre garde son nom propre (cabinet-dentaire-..., clinique-...)
  return `/${segment}/${c.slug}`;
}

/**
 * Détecte si un slug suit l'ancien format `dr-{prénom}-{nom}-{spe}-{ville}`
 * (généré par les imports Dabadoc/MedMa) et extrait ses composants en
 * matchant gourmand-to-the-right contre les sets canoniques SPECIALTIES
 * et CITIES.
 *
 * Algorithme :
 *   1. Strip le préfixe `dr-` / `dr-de-{spe}-dr-` éventuel
 *   2. Trouve le plus long suffix qui matche un city slug canonique
 *   3. Trouve le plus long suffix avant ça qui matche un spe slug canonique
 *   4. Le reste = `{prénom}-{nom}` (potentiellement multi-mots)
 *
 * Retourne `isLegacy: false` si aucun pattern ne matche → le caller décide
 * du fallback (notFound, recherche fuzzy DB, etc.).
 */
export function parseLegacySlug(slug: string): {
  isLegacy: boolean;
  nameSlug?: string;
  specialtySlug?: string;
  citySlug?: string;
} {
  if (!slug || typeof slug !== "string") return { isLegacy: false };

  // Strip préfixes parasites (dr-, dr-de-{spe}-dr-, dr-{spe}-dr-, …)
  let s = slug.toLowerCase();
  for (let i = 0; i < 3; i++) {
    const next = s.replace(/^(?:dr|pr|prof|docteur)(?:-(?:de|d))?(?:-[a-z]+)*-(?=dr-)/, "");
    if (next === s) break;
    s = next;
  }
  s = s.replace(/^(?:dr|pr|prof|docteur)-/, "");

  if (!s) return { isLegacy: false };

  const parts = s.split("-");
  if (parts.length < 2) return { isLegacy: false };

  // Greedy match city : on essaie le suffix le plus long en premier
  // (ex. "ait-melloul", "beni-mellal", "el-jadida").
  let citySlug: string | undefined;
  let cityStart = -1;
  for (let i = 0; i < parts.length; i++) {
    const candidate = parts.slice(i).join("-");
    if (CANONICAL_CITY_SLUGS.has(candidate)) {
      citySlug = candidate;
      cityStart = i;
      break;
    }
  }
  if (!citySlug) return { isLegacy: false };

  // Greedy match spe : entre cityStart-1 et le début, on cherche le suffix
  // le plus long qui matche une spécialité canonique.
  let specialtySlug: string | undefined;
  let speStart = -1;
  for (let i = 0; i < cityStart; i++) {
    const candidate = parts.slice(i, cityStart).join("-");
    if (CANONICAL_SPE_SLUGS.has(candidate)) {
      specialtySlug = candidate;
      speStart = i;
      break;
    }
  }

  // Si pas de spe matchée, on garde tout avant la ville comme nameSlug
  const nameEnd = speStart >= 0 ? speStart : cityStart;
  const nameSlug = parts.slice(0, nameEnd).join("-");
  if (!nameSlug) return { isLegacy: false };

  return {
    isLegacy: true,
    nameSlug,
    specialtySlug,
    citySlug,
  };
}

/**
 * Détecte les slugs parasites avec préfixe "dr-(de|d?-)?(spécialité)-dr-..."
 * — pollution scrapée des bases sources (Dabadoc/MedMa parfois indexent un
 * département avant le nom du médecin). Cf SEO audit 2026-05-07 P1-10.
 *
 * Exemples :
 *   - "dr-de-neurologie-dr-maroua-bouhlal-generaliste-nador"
 *   - "dr-dentiste-dr-wissame-benakka-dentiste-meknes"
 *   - "dr-cardiologue-dr-benchekroun-yasmina-cardiologue-rabat"
 *
 * Retourne `true` si le slug a un double préfixe "dr-...-dr-".
 * Le redirect P0-5 envoie déjà vers /{spec}/{ville}/{nameSlug} (clean) pour
 * ces fiches dès qu'elles ont une spé+ville canonique. Cette helper sert
 * uniquement à signaler/filtrer les slugs sales pour audit qualité.
 */
export function hasParasitePrefix(slug: string): boolean {
  if (!slug) return false;
  // Match "dr-{anything}-dr-{anything}" — 2 occurrences "dr-" en début
  return /^dr-[a-z][a-z-]*-dr-/i.test(slug);
}

/**
 * Génère l'URL canonique à partir d'un Physician (statique ou DB).
 * Choisit /medecin/... ou /{type}/... selon isCenter.
 */
export function canonicalUrl(p: Physician, clinicType?: ClinicTypeKey | null): string {
  if (p.isCenter) {
    // Sans type explicite, on l'infère exactement comme generateMetadata de
    // la fiche (centerUrl + inferClinicType) : appelé sans type, ce helper
    // renvoyait /centre/<slug>, qui répond 301 — 408 des 476 liens de centre
    // de l'annuaire des listings et 254 des 317 entrées d'ItemList
    // (relecture du 2026-09-21).
    const type = clinicType === undefined
      ? inferClinicType({ specialties: p.specialties, centerName: p.centerName ?? null, lastName: p.lastName })
      : clinicType;
    return centerUrl({ slug: p.slug, citySlug: p.citySlug, clinicType: type });
  }
  // Canonicalize p.specialties[0] avant de transmettre. Défense en profondeur :
  // physicianUrl() canonicalise déjà, mais le faire ici garantit que tout
  // consommateur direct de canonicalUrl(Physician) (sitemap, similar-doctors,
  // SimilarDoctors, JSON-LD) émet la même valeur.
  const primarySpe = p.specialties[0]
    ? canonicalSpecialty(p.specialties[0])
    : p.specialties[0];
  return physicianUrl({
    firstName: p.firstName,
    lastName: p.lastName,
    citySlug: p.citySlug,
    specialtySlug: primarySpe,
    slug: p.slug,
  });
}
